<template>
    <div class="ft-ricetta">
        <!-- intestazione ricetta -->
        <div class="ft-ricetta-header">
            <div class="ft-ricetta-info">
                <span class="ft-ricetta-icon"><b-icon icon="file-earmark-medical"></b-icon></span>
                <div class="ft-ricetta-nre">
                    <span class="ft-label">NRE</span>
                    <span class="ft-nre-value">{{ ricetta.codiceBar1 }} {{ ricetta.codiceBar2 }}</span>
                    <span class="ft-pill" v-if="ricetta.tipologiaRicetta">{{ ricetta.tipologiaRicetta }}</span>
                </div>
            </div>
            <div class="ft-ricetta-azioni" v-if="!completeBookingSsn">
                <div v-if="pallini" class="ft-pallini">
                    <div class="ft-pallino-box">
                        <label>Eseguiti</label>
                        <span class="ft-pallino ft-pallino-eseguiti">{{ totaleEseguiti }}</span>
                    </div>
                    <div class="ft-pallino-box">
                        <label>Prenotati</label>
                        <span class="ft-pallino ft-pallino-prenotati">{{ getPrenotati(ricetta.prestazioni) }}</span>
                    </div>
                    <div class="ft-pallino-box">
                        <label>Ciclo</label>
                        <span class="ft-pallino ft-pallino-ciclo">{{ ricetta.quantitaErogabile }}</span>
                    </div>
                </div>
                <span v-else-if="isCicloConcluso" class="ft-stato ft-stato-concluso"
                    title="Tutte le sedute previste sono state eseguite">
                    <b-icon icon="check-circle-fill"></b-icon>
                    Ciclo concluso
                    <b>{{ totaleEseguiti }}/{{ ricetta.quantitaErogabile }}</b>
                </span>
                <span v-else class="ft-stato" title="Eseguiti / Quantità erogabile"
                    :style="{ borderColor: coloreCiclo }">
                    Eseguiti
                    <b>{{ totaleEseguiti }}/{{ ricetta.quantitaErogabile }}</b>
                </span>
                <b-button size="sm" variant="link" class="ft-toggle" v-b-toggle="idCollapse('ricetta')"
                    title="Accessi/Appuntamenti">
                    <b-icon icon="chevron-down" class="ft-toggle-icon"></b-icon>
                </b-button>
            </div>
        </div>

        <!-- prestazioni -->
        <ul class="ft-prestazioni">
            <li v-for="(prestazione, idx) in ricetta.prestazioni" :key="prestazione.id" class="ft-prestazione">
                <div class="ft-prestazione-riga">
                    <span class="ft-prestazione-indice">{{ idx + 1 }}</span>
                    <span class="ft-codice">{{ prestazione.codicePrestazione }}</span>
                    <span class="ft-prestazione-descrizione" :title="prestazione.descrizionePrestazione">
                        {{ prestazione.descrizionePrestazione }}</span>
                    <template v-if="completeBookingSsn">
                        <div v-if="pallini" class="ft-pallini">
                            <div class="ft-pallino-box">
                                <label>Eseguiti</label>
                                <span class="ft-pallino ft-pallino-eseguiti">{{ getEseguiti([prestazione]) }}</span>
                            </div>
                            <div class="ft-pallino-box">
                                <label>Prenotati</label>
                                <span class="ft-pallino ft-pallino-prenotati">{{ getPrenotati([prestazione]) }}</span>
                            </div>
                            <div class="ft-pallino-box">
                                <label>Ciclo</label>
                                <span class="ft-pallino ft-pallino-ciclo">{{ prestazione.quantitaErogabile }}</span>
                            </div>
                        </div>
                        <span v-else-if="isPrestazioneCicloConcluso(prestazione)" class="ft-stato ft-stato-concluso"
                            title="Tutte le sedute previste sono state eseguite">
                            <b-icon icon="check-circle-fill"></b-icon>
                            <b>{{ getEseguiti([prestazione]) }}/{{ prestazione.quantitaErogabile }}</b>
                        </span>
                        <span v-else class="ft-stato" title="Appuntamenti / Quantità erogabile"
                            :style="{ borderColor: getColorePrestazione(prestazione) }">
                            <b>{{ prestazione.appuntamenti ? prestazione.appuntamenti.length : 0 }}/{{
                                prestazione.quantitaErogabile }}</b>
                        </span>
                        <b-button size="sm" variant="link" class="ft-toggle"
                            v-b-toggle="idCollapse('prestazione-' + prestazione.id)" title="Appuntamenti">
                            <b-icon icon="chevron-down" class="ft-toggle-icon"></b-icon>
                        </b-button>
                    </template>
                </div>
                <!-- con prenotazione completa: appuntamenti per prestazione -->
                <b-collapse v-if="completeBookingSsn" :id="idCollapse('prestazione-' + prestazione.id)"
                    :visible="accessiAperti">
                    <div class="ft-appuntamenti">
                        <accettazione-ssn-appuntamento-card-component v-for="appuntamento of prestazione.appuntamenti || []"
                            :key="appuntamento.id" :appuntamento="appuntamento" />
                        <div v-if="!prestazione.appuntamenti || prestazione.appuntamenti.length == 0" class="ft-vuoto">
                            Nessun appuntamento</div>
                    </div>
                </b-collapse>
            </li>
        </ul>

        <!-- senza prenotazione completa: tutti gli accessi della ricetta in ordine cronologico -->
        <b-collapse v-if="!completeBookingSsn" :id="idCollapse('ricetta')" :visible="accessiAperti">
            <div class="ft-appuntamenti">
                <accettazione-ssn-appuntamento-card-component v-for="appuntamento of appuntamentiRicetta"
                    :key="appuntamento.id" :appuntamento="appuntamento" mostraCodice />
                <div v-if="appuntamentiRicetta.length == 0" class="ft-vuoto">Nessun accesso/appuntamento</div>
            </div>
        </b-collapse>
    </div>
</template>

<script>
import AccettazioneSsnAppuntamentoCardComponent from "./AccettazioneSsnAppuntamentoCardComponent.vue";

/*
 * Card di una ricetta: NRE, tipologia, prestazioni, stato del ciclo e accessi/appuntamenti.
 * Usata da AccettazioneSsnListaComponent (dettaglio riga) e da AccettazioneSsnImpegnativaComponent (modale info).
 */
export default {
    components: { AccettazioneSsnAppuntamentoCardComponent },
    props: {
        ricetta: {
            type: Object,
            required: true,
        },
        // accessi/appuntamenti già espansi all'apertura
        accessiAperti: {
            type: Boolean,
            default: false,
        },
        // pallini eseguiti / prenotati / ciclo al posto del contatore (modale info)
        pallini: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            completeBookingSsn: this.appConfig.completeBookingSSN,
        };
    },
    computed: {
        totaleEseguiti() {
            return this.getEseguiti(this.ricetta.prestazioni);
        },
        isCicloConcluso() {
            return this.ricetta.quantitaErogabile == this.totaleEseguiti;
        },
        coloreCiclo() {
            return this.isCicloConcluso ? "#00a3ff" : "#ffc107";
        },
        // tutti gli accessi/appuntamenti della ricetta in un'unica lista, in ordine cronologico
        appuntamentiRicetta() {
            const appuntamenti = [];
            (this.ricetta.prestazioni || []).forEach((prestazione) => {
                (prestazione.appuntamenti || []).forEach((appuntamento) => {
                    appuntamenti.push({ ...appuntamento, codicePrestazione: appuntamento.codicePrestazione || prestazione.codicePrestazione });
                });
            });
            return appuntamenti.sort((a, b) => {
                const diff = (Number(a.dataOraInizio) || 0) - (Number(b.dataOraInizio) || 0);
                if (diff !== 0) return diff;
                return String(a.numeroOrdine ?? "").localeCompare(String(b.numeroOrdine ?? ""), undefined, { numeric: true });
            });
        },
    },
    methods: {
        // id univoco per istanza: la stessa ricetta può essere mostrata in lista e in modale
        idCollapse(suffisso) {
            return "ricetta-" + this._uid + "-" + this.ricetta.id + "-" + suffisso;
        },
        contaStato(prestazioni, stato) {
            if (!prestazioni) return 0;
            return prestazioni.reduce((acc, p) => acc + (p.appuntamenti ? p.appuntamenti.filter((a) => a.statoAttuale === stato).length : 0), 0);
        },
        getEseguiti(prestazioni) {
            return this.contaStato(prestazioni, "ESEGUITO");
        },
        getPrenotati(prestazioni) {
            return this.contaStato(prestazioni, "PRENOTATO");
        },
        isPrestazioneCicloConcluso(prestazione) {
            const eseguiti = this.getEseguiti([prestazione]);
            return eseguiti > 0 && eseguiti == prestazione.quantitaErogabile;
        },
        // giallo se c'è almeno un appuntamento prenotato, blu se sono tutti eseguiti
        getColorePrestazione(prestazione) {
            const appuntamenti = prestazione.appuntamenti || [];
            if (appuntamenti.some((a) => a.statoAttuale === "PRENOTATO")) {
                return "#ffc107";
            }
            if (appuntamenti.length > 0 && appuntamenti.every((a) => a.statoAttuale === "ESEGUITO")) {
                return "#00a3ff";
            }
            return "#ffffff";
        },
    },
};
</script>

<style scoped>
/* I colori seguono il tema del cliente (public/css/<cliente>); i valori dopo la virgola sono di riserva. */
/* ---- ricetta ---- */
.ft-ricetta {
    --ft-accent: var(--bg-sidebar-item, #2ba7bd);
    --ft-border: #e3e8ee;
    --ft-muted: #6c757d;
    --ft-text: #323a46;
    --ft-bg-soft: #f7f9fb;
    background: #fff;
    border: 1px solid var(--ft-border);
    border-left: 4px solid var(--ft-accent);
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    overflow: hidden;
}

.ft-ricetta-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--ft-border);
}

.ft-ricetta-info {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.ft-ricetta-icon {
    width: 30px;
    height: 30px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    color: var(--ft-accent);
    background: var(--ft-bg-soft);
    border: 1px solid var(--ft-border);
}

.ft-ricetta-nre {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
}

.ft-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--ft-muted);
}

.ft-nre-value {
    font-family: var(--font-family-monospace, monospace);
    font-size: 13px;
    font-weight: 600;
    color: var(--ft-text);
}

.ft-pill {
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: var(--bg-card-header-color-text, var(--ft-text));
    background: var(--bg-card-header-color, #eef1f4);
}

.ft-ricetta-azioni {
    display: flex;
    align-items: center;
    gap: 6px;
}

/* ---- stato ciclo ---- */
.ft-stato {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    border: 1px solid var(--ft-border);
    background: #fff;
    font-size: 11px;
    color: var(--ft-muted);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
}

.ft-stato b {
    color: var(--ft-text);
}

.ft-stato-concluso {
    color: #1e7e34;
    background: #eaf7ee;
    border-color: #b7e1c1;
}

.ft-stato-concluso b {
    color: #1e7e34;
}

/* ---- pallini eseguiti / prenotati / ciclo ---- */
.ft-pallini {
    display: flex;
    flex-shrink: 0;
    align-items: flex-end;
    gap: 10px;
}

.ft-pallino-box {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.ft-pallino-box label {
    margin-bottom: 2px;
    font-size: 10px;
    color: var(--ft-muted);
}

.ft-pallino {
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 25px;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.ft-pallino-eseguiti {
    background: #218838;
}

.ft-pallino-prenotati {
    background: #ffc107;
}

.ft-pallino-ciclo {
    background: #00a3ff;
}

/* ---- toggle ---- */
.ft-toggle {
    width: 28px;
    height: 28px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    color: var(--ft-muted);
}

.ft-toggle:hover,
.ft-toggle:focus {
    color: var(--ft-accent);
    background: var(--ft-bg-soft);
    box-shadow: none;
}

.ft-toggle-icon {
    transition: transform 0.2s ease-in-out;
}

.ft-toggle[aria-expanded="true"] .ft-toggle-icon {
    transform: rotate(180deg);
}

/* ---- prestazioni ---- */
.ft-prestazioni {
    list-style: none;
    margin: 0;
    padding: 4px 0;
}

.ft-prestazione + .ft-prestazione {
    border-top: 1px dashed var(--ft-border);
}

.ft-prestazione-riga {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 12px;
    min-width: 0;
}

.ft-prestazione-indice {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 10px;
    font-weight: 700;
    color: var(--ft-muted);
    background: var(--ft-bg-soft);
    border: 1px solid var(--ft-border);
}

.ft-codice {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 4px;
    font-family: var(--font-family-monospace, monospace);
    font-size: 12px;
    font-weight: 600;
    color: var(--ft-accent);
    background: var(--ft-bg-soft);
    border: 1px solid var(--ft-border);
}

.ft-prestazione-descrizione {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    color: var(--ft-text);
}

/* ---- accessi / appuntamenti ---- */
.ft-appuntamenti {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 12px;
    background: var(--ft-bg-soft);
    border-top: 1px solid var(--ft-border);
}

.ft-vuoto {
    font-size: 11px;
    font-style: italic;
    color: var(--ft-muted);
}
</style>
